<template>
	<div class="omapi-statswidget omapi-card__flex">
		<div class="omapi-statswidget__heading flex">
			<common-tooltip tooltip="A subscriber is any lead generated through an OptinMonster form.">
				<h5>Number of Subscribers</h5>
			</common-tooltip>

			<common-date-select
				:min-date="minDate"
				:filter="filter"
				@quickSelect="updateFilter"
				@dateSelection="fetchStatsFromDateRange"
			/>
		</div>
		<slot></slot>
		<common-card size="half" class="omapi-statswidget__stats omapi-card__no-padding">
			<div class="omapi-statswidget__results omapi-multi-column__primary">
				<div class="omapi-statswidget__desc">Subscribers</div>
				<div class="omapi-statswidget__stat">{{ statsCurrent }}</div>
				<div class="omapi-statswidget__meta">Results based on {{ datePrettyText }}</div>
			</div>
			<div
				class="omapi-statswidget__change omapi-multi-column__secondary"
				:class="{
					positive: changePositive,
					negative: changeNegative,
				}"
			>
				{{ percent }}%
			</div>
		</common-card>
		<common-card size="half" class="omapi-statswidget__resources">
			<div class="omapi-multi-column__secondary omapi-icon-wrapper">
				<img src="../../../assets/images/icons/trulead-icon-isometric.svg" alt="TruLead® by OptinMonster" />
			</div>
			<div class="omapi-multi-column__primary">
				<div class="omapi-multi-column__title">Discover Lead Verification</div>
				<div class="omapi-multi-column__text">
					Intelligently verify leads captured through OptinMonster forms with Lead Verification and TruLead®.
				</div>
				<div class="omapi-multi-column__buttons">
					<core-button
						:href="
							$urls.app(
								'leads/verification/?utm_source=WordPress&utm_medium=MonsterLeads&utm_campaign=Plugin'
							)
						"
						target="_blank"
						rel="noopener"
						type="secondary"
						size="small"
						>Learn More</core-button
					>
				</div>
			</div>
		</common-card>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
	data() {
		return {
			minDate: new Date(2019, 0), // Monster Leads didn't exist before this!
		};
	},
	computed: {
		...mapGetters('monsterleads', ['dateRange', 'statsCurrent', 'statsChange']),
		filter() {
			return this.dateRange.filter || '30-days';
		},
		percent() {
			return parseFloat(this.statsChange).toFixed(2);
		},
		changePositive() {
			return 0 < this.percent;
		},
		changeNegative() {
			return 0 > this.percent;
		},
		datePrettyText() {
			switch (this.filter) {
				case '30-days':
					return "'Last 30 Days'";
				case '7-days':
					return "'Last 7 Days'";
				case 'custom':
					return 'selected date range';
			}
			return '';
		},
	},
	mounted() {
		this.fetchStatsFromDateRange({
			filter: this.filter,
			start: '',
			end: '',
		});
	},
	methods: {
		...mapActions('monsterleads', ['fetchStatsFromDateRange']),
		updateFilter(filter) {
			if ('custom' === filter) {
				// Only update filter, fetch comes after date-selection.
				return this.$store.commit('monsterleads/updateDateRange', {
					...this.dateRange,
					filter,
				});
			}

			this.fetchStatsFromDateRange({
				filter,
				start: '',
				end: '',
			});
		},
	},
};
</script>

<style lang="scss">
@import '../../../assets/scss/variables';
.omapi-screen .omapi-statswidget {
	flex-basis: 100%;
	&__heading {
		flex-basis: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin: 14px 0 20px;
		h5 {
			font-size: 16px;
			line-height: 140%;
			margin: 0;
		}

		.omapi-dropdown__select .omapi-dropdown__toggle {
			width: 168px;
			margin: 0;
		}
	}
	&__stats {
		.omapi-card-content {
			display: flex;
			height: 100%;
		}
		@media screen and (max-width: 1045px) {
			flex-basis: 100%;
		}
	}
	&__desc {
		font-size: 16px;
		font-weight: $font_bold;
		color: $font_secondary-color;
		line-height: 1.4em;
		margin: 0;
	}
	&__results {
		padding: 38px 87px;
		@media screen and (max-width: 1300px) {
			padding: 38px;
		}
	}
	&__meta {
		color: $font_secondary-color;
	}
	&__change {
		padding: 55px 61px;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		font-size: 26px;
		font-weight: bold;
		line-height: 1.3em;
		background-color: $color_white-button-background;
		color: $font_dark-color;
		&:before {
			margin-bottom: 5px;
		}
		&.positive {
			color: $color_green-primary;
			&:before {
				content: url('../../../assets/images/icons/arrow-up.svg');
			}
		}
		&.negative {
			color: $font_secondary-color;
			&:before {
				content: url('../../../assets/images/icons/arrow-down.svg');
			}
		}
		@media screen and (max-width: 1300px) {
			padding: 55px 45px;
		}
	}
	&__resources {
		.omapi-card-content {
			display: flex;
			padding: 37px 54px 43px;
			align-items: center;
			.omapi-icon-wrapper {
				flex-basis: 114px;
				padding-right: 30px;
				flex-grow: 0;
				flex-shrink: 0;
				img {
					max-width: 114px;
					width: 114px;
					height: auto;
				}
			}
			.omapi-multi-column__title {
				font-weight: $font_bold;
				line-height: 1.5em;
			}
			.omapi-multi-column__text {
				color: $color_black;
				line-height: 1.5em;
				font-size: 14px;
			}
			.omapi-multi-column__buttons .omapi-button__small {
				padding: 6px 8px;
				line-height: 1em;
			}
		}
		@media screen and (max-width: 1045px) {
			flex-basis: 100%;
		}
	}
	&__stat {
		font-size: 48px;
		font-weight: $font_bold;
		line-height: 1.2em;
	}
	&__desc {
		color: $font_secondary-color;
		font-size: 18px;
		margin-top: 5px;
	}
}
</style>
